// Betaling efter udført opgave: kunden får en mail (automatiseringen "Betal MobilePay & kort", hændelsen "Markeret færdig") med
// MobilePay-nummeret (bookingnummeret som reference), et kortlink til Viva Wallet og en kvittering for arbejdet.
//  • Indstillingerne (Indstillinger › Betaling) gælder virksomheden; et brand kan have sine egne (tom = virksomhedens).
//  • Kortlinket er det faste link fra indstillingerne – eller et personligt link pr. booking, når Viva-nøglerne er sat på
//    serveren (supabase/functions/_shared/viva.ts). Fakturagebyret er det eksisterende gebyr (Indstillinger › Gebyrer).
//  • Kvitteringen ({{kvittering}}) er ren tekst med overskriften "Kvittering"; mail.ts viser den som en pæn kvitteringsblok.
// Ingen afhængigheder ud over pris/dato/fejl, så edge-funktionerne (Deno) kan bruge filen direkte.
import { kr, moms } from './pris.ts';
import { datoTekst } from './dato.ts';
import { fejl } from './fejl.ts';

export type BetalingIndst = {
  mobilepay: string;   // MobilePay-nummer (fx 501150)
  vivaLink: string;    // fast betalingslink til Viva Wallet (fx https://pay.vivawallet.com/firma)
  telefontid: string;  // hvornår kunden kan ringe, fx "alle hverdage kl. 9–16"
};
export const STANDARD_BETALING: BetalingIndst = { mobilepay: '', vivaLink: '', telefontid: '' };

const r = (s: unknown, n: number) => String(s ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, n);

/** Tjek og ryd betalingsoplysningerne før de gemmes. Kaster en fejl med felterne, der er galt. */
export function normBetaling(b: Partial<BetalingIndst> | null | undefined): BetalingIndst {
  const f: Record<string, string> = {};
  const mobilepay = r(b?.mobilepay, 20).replace(/\s/g, '');
  if (mobilepay && !/^\d{4,10}$/.test(mobilepay)) f.mobilepay = 'Skriv MobilePay-nummeret med 4–10 cifre.';
  const vivaLink = r(b?.vivaLink, 300);
  if (vivaLink && !/^https:\/\/[^\s"<>]+\.[^\s"<>]+$/.test(vivaLink)) f.vivaLink = 'Skriv et link, der starter med https://';
  if (Object.keys(f).length) throw fejl('Tjek betalingsoplysningerne.', f);
  return { mobilepay, vivaLink, telefontid: r(b?.telefontid, 80) };
}

/** Brandets egne betalingsoplysninger (tomme felter = virksomhedens). */
export function betalingFor(firma: Partial<BetalingIndst> | null | undefined, brand?: { mobilepay?: string; viva?: string; telefontid?: string } | null): BetalingIndst {
  const v = { ...STANDARD_BETALING, ...(firma ?? {}) };
  return { mobilepay: brand?.mobilepay || v.mobilepay, vivaLink: brand?.viva || v.vivaLink, telefontid: brand?.telefontid || v.telefontid };
}

/** Fakturagebyret som tekst ("29 kr.") – det er momsfrit; tom, hvis der ikke er noget gebyr. */
export const fakturagebyrTekst = (beloeb: number) => (beloeb > 0 ? kr(beloeb) : '');

// ---------- Kvittering ----------
export type KvitteringLinje = { navn: string; beloeb: number };   // øre ekskl. moms
export type Kvittering = {
  booking_nr: string; dato: string; adresse: string; bil: string; nummerplade: string; steamer: string;
  linjer: KvitteringLinje[];   // ydelse, tilvalg og gebyrer (før rabat)
  rabat: number; pris: number; moms: number; total: number; momsProcent: number;   // pris = ekskl. moms efter rabat
  firma: { navn: string; cvr: string; adresse: string };
};
export const KVITTERING_OVERSKRIFT = 'Kvittering';
/** Linjen mellem kvitteringens afsnit (ydelser, beløb, firmaet). */
export const KVITTERING_SKILLE = '—';

/** Kvitteringens linjer ud fra bookingens tal: ydelsen er det, der er tilbage, når tilvalg og gebyrer er trukket fra (før rabat). */
export function kvitteringLinjer(b: { ydelse: string; pris: number; rabat: number; tilvalg: { navn: string; antal: number; pris: number }[]; gebyrer: { navn: string; beloeb: number }[] }): KvitteringLinje[] {
  const tv = b.tilvalg.reduce((s, t) => s + t.pris, 0), gb = b.gebyrer.reduce((s, g) => s + g.beloeb, 0);
  return [
    { navn: b.ydelse || 'Ydelse', beloeb: b.pris + b.rabat - tv - gb },
    ...b.tilvalg.map((t) => ({ navn: (t.antal > 1 ? `${t.antal} × ` : '') + t.navn, beloeb: t.pris })),
    ...b.gebyrer.map((g) => ({ navn: g.navn, beloeb: g.beloeb })),
  ];
}

/** Kvitteringen som tekst: info, linjer, beløb og firmaet – adskilt af en streg. mail.ts genkender overskriften. */
export function kvitteringTekst(k: Kvittering): string {
  const navn = (s: string) => s.replace(/:/g, ' ').replace(/\s+/g, ' ').trim();   // et kolon i et navn ville dele linjen forkert
  const info = [
    k.dato && `Dato: ${datoTekst(k.dato, true, 0)}`,
    k.adresse && `Adresse: ${k.adresse}`,
    (k.bil || k.nummerplade) && `Bil: ${[k.bil, k.nummerplade && (k.bil ? `(${k.nummerplade})` : k.nummerplade)].filter(Boolean).join(' ')}`,
    k.steamer && `Udført af: ${k.steamer}`,
    `Bookingnr.: ${k.booking_nr}`,
  ].filter(Boolean) as string[];
  const linjer = [...k.linjer.filter((l) => l.beloeb !== 0 || l === k.linjer[0]).map((l) => `${navn(l.navn)}: ${kr(l.beloeb)}`), ...(k.rabat > 0 ? [`Rabat: ${kr(-k.rabat)}`] : [])];
  const beloeb = [`I alt ekskl. moms: ${kr(k.pris)}`, `Total inkl. moms: ${kr(k.total)}`, `Heraf moms ${k.momsProcent} %: ${kr(k.moms)}`];
  const firma = [k.firma.navn, k.firma.cvr && `CVR ${k.firma.cvr}`, k.firma.adresse].filter(Boolean).join(' · ');
  return [KVITTERING_OVERSKRIFT, ...info, KVITTERING_SKILLE, ...linjer, KVITTERING_SKILLE, ...beloeb, ...(firma ? [KVITTERING_SKILLE, firma] : [])].join('\n');
}

/** Eksempel til forhåndsvisningen (samme kunde og booking som eksempelVariabler i automation.ts). */
export function eksempelKvittering(firma: { navn: string; cvr?: string; adresse?: string }, momsProcent = 25): Kvittering {
  const linjer = [{ navn: 'Indvendig rens', beloeb: 96000 }, { navn: 'Lugtfjerner', beloeb: 15920 }];   // total 1.299 kr. som eksempelVariabler
  const pris = linjer.reduce((s, l) => s + l.beloeb, 0) - 8000, m = moms(pris, momsProcent);
  return { booking_nr: '10482', dato: '2026-11-04', adresse: 'Vesterbrogade 12, 1620 København V', bil: 'Tesla Model Y', nummerplade: 'AB 12 345', steamer: 'Jamil',
    linjer, rabat: 8000, pris, moms: m, total: pris + m, momsProcent, firma: { navn: firma.navn, cvr: firma.cvr ?? '', adresse: firma.adresse ?? '' } };
}
